const DEFAULT_MARKER_ICON = 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png';
const DEFAULT_POPUP_OFFSET_X = 120;
const DEFAULT_POPUP_OFFSET_Y = -14;
const MOBILE_POPUP_BREAKPOINT = 767;
const MOBILE_POPUP_OFFSET_X = 140;
const DEFAULT_CENTER_OFFSET_X = 0;
const DEFAULT_CENTER_OFFSET_Y = 0;

const MAP_DEFAULTS = {
  zoom: 6,
  cityZoom: 10,
  locationZoom: 16,
  center: { lat: 53.8973, lng: -2.9301 },
  maxZoom: 18,
  minZoom: 5,
};

const waitForGoogleMaps = () => {
  return new Promise((resolve) => {
    const checkGoogle = () => {
      if (window.google?.maps?.Map) {
        resolve();
        return;
      }

      setTimeout(checkGoogle, 100);
    };

    checkGoogle();
  });
};

const getCoordsFromElement = (element) => {
  const lat = Number.parseFloat(element.getAttribute('data-lat'));
  const lng = Number.parseFloat(element.getAttribute('data-lng'));

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
};

const getPopupHtml = (addressLink) => {
  const subTitle = addressLink.querySelector('.sub-title')?.textContent?.trim() || '';
  const name = addressLink.querySelector('.name')?.textContent?.trim() || '';
  const address = addressLink.querySelector('address')?.textContent?.trim() || '';

  return `
    <div class="map-popup">
      <div class="address-info">
        <span class="icon">
          <img src="images/icon-pin.svg" alt="pin" width="16" height="16" />
        </span>
        <span class="inner">
          <span class="sub-title">${subTitle}</span>
          <strong class="name">${name}</strong>
          <address>${address}</address>
        </span>
      </div>
    </div>
  `;
};

const toInt = (value, fallback) => {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const resolveTabSelectorFromHash = (hashValue, locationButtons) => {
  const normalizedHash = hashValue.replace('#', '').trim().toLowerCase();
  if (!normalizedHash) return null;

  for (const button of locationButtons) {
    const selector = button.getAttribute('href');
    if (!selector || !selector.startsWith('#')) continue;

    const normalizedSelector = selector.replace('#', '').trim().toLowerCase();
    const shortSelector = normalizedSelector.replace(/-tabs$/, '');

    if (normalizedHash === normalizedSelector || normalizedHash === shortSelector) {
      return `#${normalizedSelector}`;
    }
  }

  return null;
};

const clearActiveAddress = (mapRow) => {
  mapRow.querySelectorAll('.map-list li.active').forEach((item) => {
    item.classList.remove('active');
  });
};

const moveMap = (map, coords, zoom, offsetX = 0, offsetY = 0) => {
  map.setZoom(zoom);
  
  if (!offsetX && !offsetY) {
    map.setCenter(coords);
    return;
  }

  const projection = map.getProjection();
  if (!projection) {
    map.setCenter(coords);
    return;
  }

  const targetLatLng = new google.maps.LatLng(coords.lat, coords.lng);
  const scale = (window.innerWidth < MOBILE_POPUP_BREAKPOINT ? 2.08 : 2.02) ** zoom;
  const worldPoint = projection.fromLatLngToPoint(targetLatLng);
  const adjustedCenter = new google.maps.Point(
    worldPoint.x + (offsetX / scale),
    worldPoint.y - (offsetY / scale),
  );

  map.setCenter(projection.fromPointToLatLng(adjustedCenter));
};

const initializeMap = async () => {
  const mapElement = document.getElementById('map-custom');
  const mapRow = document.querySelector('.map-row');
  if (!mapElement || !mapRow) return;

  try {
    await waitForGoogleMaps();

    const map = new google.maps.Map(mapElement, {
      zoom: MAP_DEFAULTS.zoom,
      center: MAP_DEFAULTS.center,
      gestureHandling: 'cooperative',
      zoomControl: true,
      maxZoom: MAP_DEFAULTS.maxZoom,
      minZoom: MAP_DEFAULTS.minZoom,
      tilt: 0,
      disableDefaultUI: true,
    });

    const markerIcon = mapElement.dataset.markerIcon || DEFAULT_MARKER_ICON;
    const popupOffsetX = toInt(mapElement.dataset.popupOffsetX, DEFAULT_POPUP_OFFSET_X);
    const popupOffsetY = toInt(mapElement.dataset.popupOffsetY, DEFAULT_POPUP_OFFSET_Y);
    const effectivePopupOffsetX = window.innerWidth < MOBILE_POPUP_BREAKPOINT ? MOBILE_POPUP_OFFSET_X : popupOffsetX;
    const centerOffsetX = toInt(mapElement.dataset.centerOffsetX, DEFAULT_CENTER_OFFSET_X);
    const centerOffsetY = toInt(mapElement.dataset.centerOffsetY, DEFAULT_CENTER_OFFSET_Y);
    const effectiveCenterOffsetX = centerOffsetX || popupOffsetX;
    const effectiveCenterOffsetY = centerOffsetY;
    const isAllRegionsMode = mapElement.dataset.mapMode === 'all-regions';

    const addressLinks = Array.from(mapRow.querySelectorAll('.map-list .address-info[data-lat][data-lng]'));
    const locationButtons = Array.from(mapRow.querySelectorAll('.location-btn'));

    const infoWindow = new google.maps.InfoWindow({
      disableAutoPan: true,
      pixelOffset: new google.maps.Size(effectivePopupOffsetX, popupOffsetY),
    });

    const markerByAddress = new Map();
    const tabAddresses = new Map();
    let currentMarker = null;

    locationButtons.forEach((button) => {
      const tabSelector = button.getAttribute('href');
      if (!tabSelector) return;
      tabAddresses.set(tabSelector, Array.from(mapRow.querySelectorAll(`${tabSelector} .map-list .address-info[data-lat][data-lng]`)));
    });

    addressLinks.forEach((addressLink) => {
      const coords = getCoordsFromElement(addressLink);
      if (!coords) return;

      const marker = new google.maps.Marker({
        position: coords,
        icon: markerIcon,
      });

      marker.addListener('click', () => {
        selectAddress(addressLink);
      });

      markerByAddress.set(addressLink, marker);
    });

    const setVisibleAddresses = (visibleLinks) => {
      const visibleSet = new Set(visibleLinks);

      markerByAddress.forEach((marker, addressLink) => {
        marker.setMap(visibleSet.has(addressLink) ? map : null);
      });
    };

    const clearSelectedLocation = () => {
      clearActiveAddress(mapRow);
      infoWindow.close();

      if (!isAllRegionsMode && currentMarker) {
        currentMarker.setMap(null);
        currentMarker = null;
      }
    };

    const showAllLocations = () => {
      clearSelectedLocation();
      setVisibleAddresses(addressLinks);
      moveMap(map, MAP_DEFAULTS.center, MAP_DEFAULTS.zoom);
    };

    const showLocationsByTab = (tabSelector, shouldFocusCity = true) => {
      const scopedLinks = tabAddresses.get(tabSelector) || [];
      if (!scopedLinks.length) {
        showAllLocations();
        return;
      }

      clearSelectedLocation();
      setVisibleAddresses(scopedLinks);

      if (!shouldFocusCity) return;

      const firstCoords = getCoordsFromElement(scopedLinks[0]);
      if (!firstCoords) return;

      moveMap(map, firstCoords, MAP_DEFAULTS.cityZoom);
    };

    const selectAddress = (addressLink) => {
      const coords = getCoordsFromElement(addressLink);
      if (!coords) return;

      clearActiveAddress(mapRow);

      const item = addressLink.closest('li');
      if (item) {
        item.classList.add('active');
      }

      moveMap(map, coords, MAP_DEFAULTS.locationZoom, effectiveCenterOffsetX, effectiveCenterOffsetY);

      const marker = markerByAddress.get(addressLink);
      if (!marker) {
        return;
      }

      if (!isAllRegionsMode && currentMarker && currentMarker !== marker) {
        currentMarker.setMap(null);
      }

      if (!marker.getMap()) {
        marker.setMap(map);
      }

      currentMarker = marker;

      infoWindow.setContent(getPopupHtml(addressLink));
      infoWindow.open({
        map,
        anchor: marker,
        shouldFocus: false,
      });
    };

    addressLinks.forEach((addressLink) => {
      addressLink.addEventListener('click', (event) => {
        event.preventDefault();
        selectAddress(addressLink);
      });
    });

    locationButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const tabSelector = button.getAttribute('href');
        if (!tabSelector) return;

        if (isAllRegionsMode) {
          showLocationsByTab(tabSelector);
          return;
        }

        clearSelectedLocation();
        const firstAddress = tabAddresses.get(tabSelector)?.[0];
        if (!firstAddress) return;

        const firstCoords = getCoordsFromElement(firstAddress);
        if (!firstCoords) return;

        moveMap(map, firstCoords, MAP_DEFAULTS.cityZoom);
      });
    });

    if (isAllRegionsMode) {
      window.addEventListener('hashchange', () => {
        const targetSelector = resolveTabSelectorFromHash(window.location.hash, locationButtons);
        if (targetSelector) {
          showLocationsByTab(targetSelector);
          return;
        }

        showAllLocations();
      });

      const initialSelector = resolveTabSelectorFromHash(window.location.hash, locationButtons);
      if (initialSelector) {
        showLocationsByTab(initialSelector);
      } else {
        showAllLocations();
      }
    } else {
      clearSelectedLocation();
    }

    google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
      mapElement.classList.add('map-loaded');
    });

  } catch (error) {
    console.error('Failed to initialize map:', error);
    mapElement.textContent = 'Failed to load map. Please try again later.';
  }
};

const initMap = () => {
  initializeMap();
};

window.initMap = initMap;

export default initMap;
